/**
 * SortSelect Component
 *
 * A compact dropdown for sorting reviews
 * Styled according to the design system with rounded full border
 */

'use client';

import { FC, useState, useRef, useEffect } from 'react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { cn } from '@/src/shared/lib/utils';

export interface SortOption {
  value: string;
  label: string;
}

export interface SortSelectProps {
  /**
   * List of sort options
   */
  options: SortOption[];

  /**
   * Currently selected value
   */
  value: string;

  /**
   * Change handler
   */
  onChange: (value: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const SortSelect: FC<SortSelectProps> = ({
  options,
  value,
  onChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'h-12 pl-5 pr-3 py-3 bg-background-primary rounded-full',
          'shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)]',
          'border border-accent-secondary',
          'inline-flex justify-between items-center gap-2.5',
          'min-w-[280px]',
          'transition-colors duration-200',
          'hover:border-accent-primary'
        )}
      >
        <Typography variant="bodySm" color="primary">
          {selectedOption?.label || options[0]?.label}
        </Typography>
        <Icon
          name={isOpen ? 'arrow-up' : 'arrow-down'}
          size="lg"
          color="primary"
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute top-full left-0 right-0 mt-2 z-50',
            'bg-background-primary rounded-2xl',
            'border border-accent-secondary',
            'shadow-lg',
            'overflow-hidden'
          )}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'w-full px-5 py-3 text-left',
                'transition-colors duration-150',
                option.value === value
                  ? 'bg-accent-tertiary'
                  : 'hover:bg-surface-hover'
              )}
            >
              <Typography variant="bodySm" color="primary">
                {option.label}
              </Typography>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
